import React from 'react';

import { faCarSide, faEdit, faTrashAlt, faInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { badgeMembershipCardOptions, VEHICLE_PARKING_STATUS } from 'src/features/memberships/utils/constants';
import { GeneralBlock, GeneralBlockBody, GeneralBlockValue } from 'src/registry/components/blocks/GeneralBlock.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';

import type { InParkingCard } from 'src/features/cards/types';
import type { MembershipGroupCard } from 'src/features/memberships/types';
import { TooltipProvider, TooltipTrigger, Tooltip, TooltipContent } from 'src/registry/ui/Tooltip';

type MembershipGroupCardProps = {
    card: MembershipGroupCard;
    inParkingCards?: InParkingCard[];
    isLoadingInParkingCards?: boolean;
    setSelectedDeleteMembershipGroupCard: (card: MembershipGroupCard) => void;
    setSelectedMembershipGroupCard: (card: MembershipGroupCard) => void;
    canSeeChangeCardButtons: boolean;
    isClient: boolean;
};
function MembershipGroupCardBlock({
    card,
    inParkingCards,
    isLoadingInParkingCards,
    setSelectedDeleteMembershipGroupCard,
    setSelectedMembershipGroupCard,
    canSeeChangeCardButtons,
    isClient,
}: MembershipGroupCardProps) {
    // Check if the card is part of the parking
    const isInParkingOption = React.useMemo(() => {
        if (!inParkingCards) return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.OUT_OF_PARKING];
        const foundCard = inParkingCards.find((inParkingCard) => inParkingCard.card_number === card.number);
        if (foundCard) {
            return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.IN_PARKING];
        } else {
            return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.OUT_OF_PARKING];
        }
    }, [inParkingCards]);

    const { formatMessage } = useIntl();
    return (
        <GeneralBlock
            className="group flex min-h-24 flex-col items-center gap-4 text-center break-all lg:flex-row"
            key={`${card.membership_group_id}-${card.id}`}
        >
            <SquareIcon className="right-4" icon={faCarSide} />
            <div className="flex w-full flex-col items-center gap-2 lg:flex-row lg:justify-between lg:text-left">
                <div className="flex flex-col items-center lg:items-start">
                    <GeneralBlockBody variant="dashboard" className="gap-1 lg:flex-col">
                        <GeneralBlockValue size="small" className="font-mono">
                            {card.number}
                        </GeneralBlockValue>
                        {!isClient && card.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {card.tags.map((tag) => {
                                    return (
                                        <Badge key={`card-tag-${tag.id}`} theme={tag.card_tag.theme}>
                                            {tag.card_tag.text}
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </GeneralBlockBody>
                </div>

                {isLoadingInParkingCards ? (
                    <Skeleton className="h-6 w-24 rounded-full" />
                ) : (
                    <Badge
                        className={`text-nowrap ${canSeeChangeCardButtons && 'md:group-hover:hidden'}`}
                        theme={isInParkingOption.theme}
                    >
                        {formatMessage(isInParkingOption.label)}
                    </Badge>
                )}

                {canSeeChangeCardButtons && (
                    <div className="mt-2 flex shrink-0 gap-2 sm:mt-0 sm:hidden sm:group-hover:flex lg:ml-auto">

                        {card?.description && <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="outline">
                                        <FontAwesomeIcon icon={faInfo}/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {card?.description}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>}

                        <Button onClick={() => setSelectedMembershipGroupCard(card)} size="icon" variant="outline">
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>

                        <Button
                            onClick={() => setSelectedDeleteMembershipGroupCard(card)}
                            size="icon"
                            variant="destructiveLight"
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                    </div>
                )}
            </div>
        </GeneralBlock>
    );
}

export { MembershipGroupCardBlock };
